import { getMapRoute } from './route.service';

/**
 * Route caching service for offline route availability
 * Uses Cache API to store pre-computed routes for tours
 */

const ROUTE_CACHE_NAME = 'tour-routes-cache';

/**
 * Generate a unique cache key for a route segment
 */
function generateRouteCacheKey(
  tourId: string,
  waypoints: [number, number][],
  profile: string,
  segmentIndex: number
): string {
  // Create a deterministic hash from waypoints
  const waypointsHash = waypoints
    .map(([lng, lat]) => `${lng.toFixed(6)},${lat.toFixed(6)}`)
    .join('_');

  return `route_${tourId}_${profile}_segment${segmentIndex}_${waypointsHash}`;
}

/**
 * Cache a route for offline use
 */
export async function cacheRoute(
  tourId: string,
  waypoints: [number, number][],
  profile: string,
  routeData: any,
  segmentIndex: number = 0
): Promise<void> {
  try {
    const cache = await caches.open(ROUTE_CACHE_NAME);
    const cacheKey = generateRouteCacheKey(tourId, waypoints, profile, segmentIndex);
    const url = `https://offline-route-cache/${cacheKey}`;

    // Create a Response object with the route data
    const response = new Response(JSON.stringify(routeData), {
      headers: {
        'Content-Type': 'application/json',
        'X-Tour-ID': tourId,
        'X-Route-Profile': profile,
        'X-Cached-Date': new Date().toISOString(),
      },
    });

    await cache.put(url, response);
  } catch (error) {
    console.error('Failed to cache route:', error);
    // Don't throw - caching failure shouldn't break the app
  }
}

/**
 * Retrieve a cached route
 */
export async function getCachedRoute(
  tourId: string,
  waypoints: [number, number][],
  profile: string,
  segmentIndex: number = 0
): Promise<any | null> {
  try {
    const cache = await caches.open(ROUTE_CACHE_NAME);
    const cacheKey = generateRouteCacheKey(tourId, waypoints, profile, segmentIndex);
    const url = `https://offline-route-cache/${cacheKey}`;

    const cachedResponse = await cache.match(url);

    if (cachedResponse) {
      const routeData = await cachedResponse.json();
      return routeData;
    }

    return null;
  } catch (error) {
    console.warn('Failed to retrieve cached route:', error);
    return null;
  }
}

/**
 * Pre-compute and cache all routes for a tour
 * Progress range: 86-90% of download progress
 */
export async function preComputeTourRoutes(
  tourId: string,
  stops: any[],
  onProgress?: (progress: number) => void
): Promise<{ success: number; failed: number }> {
  // Same waypoint processing logic as fetchRoutes()
  const tourWaypoints_latLngReversed: {
    type: string;
    langAndLang: [number, number];
  }[] =
    stops
      .filter((stop) => stop.include_path !== false)
      .sort((a, b) => a?.order - b?.order)
      .map((stop) => ({
        type: normalizeDirectionType(stop?.direction_type),
        langAndLang: [
          stop?.stop_location?.longitude,
          stop?.stop_location?.latitude,
        ],
      })) || [];

  if (!tourWaypoints_latLngReversed?.length) {
    // Still report 90% to complete the progress range
    if (onProgress) onProgress(90);
    return { success: 0, failed: 0 };
  }

  // Group waypoints by direction type
  const groups: { type: string; waypoints: typeof tourWaypoints_latLngReversed }[] = [];
  let lastType: string | null = null;

  tourWaypoints_latLngReversed.forEach((waypoint) => {
    if (waypoint.type !== lastType) {
      groups.push({ type: waypoint.type, waypoints: [waypoint] });
      lastType = waypoint.type;
    } else {
      groups[groups.length - 1].waypoints.push(waypoint);
    }
  });

  // Filter out "none" type segments
  const routeGroups = groups.filter((group) => group.type !== 'none');

  if (routeGroups.length === 0) {
    if (onProgress) onProgress(90);
    return { success: 0, failed: 0 };
  }

  let successCount = 0;
  let failedCount = 0;

  // Fetch and cache each route segment
  // Progress: 86% -> 90% (4% range)
  const PROGRESS_START = 86;
  const PROGRESS_END = 90;
  const PROGRESS_RANGE = PROGRESS_END - PROGRESS_START;

  for (let i = 0; i < routeGroups.length; i++) {
    const group = routeGroups[i];
    const waypoints = group.waypoints.map((w) => w.langAndLang);

    try {
      const routeData = await getMapRoute(waypoints, group.type);

      if (routeData) {
        await cacheRoute(tourId, waypoints, group.type, routeData, i);
        successCount++;
      } else {
        failedCount++;
      }
    } catch (error) {
      console.error(`Failed to fetch route segment ${i}:`, error);
      failedCount++;
    }

    // Report progress within 86-90% range
    if (onProgress) {
      const segmentProgress = ((i + 1) / routeGroups.length) * PROGRESS_RANGE;
      const currentProgress = PROGRESS_START + segmentProgress;
      onProgress(Math.round(currentProgress));
    }
  }

  // Ensure we reach 90%
  if (onProgress) onProgress(90);

  return { success: successCount, failed: failedCount };
}

/**
 * Delete all cached routes for a specific tour
 */
export async function deleteTourRoutes(tourId: string): Promise<void> {
  try {
    const cache = await caches.open(ROUTE_CACHE_NAME);
    const requests = await cache.keys();

    for (const request of requests) {
      // Check if this route belongs to the tour
      if (request.url.includes(`route_${tourId}_`)) {
        await cache.delete(request);
      }
    }
  } catch (error) {
    console.error('Failed to delete tour routes:', error);
  }
}

/**
 * Get cached route count for a tour
 */
export async function getCachedRouteCount(tourId: string): Promise<number> {
  try {
    const cache = await caches.open(ROUTE_CACHE_NAME);
    const requests = await cache.keys();

    return requests.filter((request) => request.url.includes(`route_${tourId}_`)).length;
  } catch (error) {
    console.warn('Failed to get cached route count:', error);
    return 0;
  }
}

/**
 * Normalize direction type to OpenRouteService profile
 */
function normalizeDirectionType(type: string | undefined): string {
  if (!type) return 'cycling-regular';

  switch (type) {
    case 'car':
      return 'driving-car';
    case 'foot':
      return 'foot-walking';
    case 'bike':
      return 'cycling-regular';
    case 'none':
      return 'none';
    default:
      return 'cycling-regular';
  }
}
